<template>
  <gmap-map ref="map" class="map" :zoom="9" :center="{ lat: -33.867001, lng:151.209113 }"/>
</template>

<style scoped>
.map {
  width: 100%;
  height: 100%;
}
</style>

<script>
import { get, sync } from 'vuex-pathify'
import { Map as GmapMap, gmapApi } from 'gmap-vue'
import hash from 'object-hash'
// import localforage from 'localforage'
import { chunk } from 'lodash'

export default {
  components: {
    GmapMap
  },

  data () {
    return {
      renderDepotOnMap: true,
      directions: {
        service: null
      },
      markers: [],
      polyline: null,
      map: null
    }
  },

  computed: {
    ...get('deliveryRuns', ['deliveryRun']),
    triggerUpdateMapDirections: sync('deliveryRuns/triggers@updateMapDirections'),

    google: gmapApi,

    depotLatLng () {
      if (!this.google) {
        return null
      }

      return new this.google.maps.LatLng(process.env.VUE_APP_DEPOT_LATITUDE, process.env.VUE_APP_DEPOT_LONGITUDE)
    },

    bounds () {
      if (!this.google) {
        return []
      }

      const bounds = new this.google.maps.LatLngBounds()

      this.deliveryRun.stops.forEach(({ station: { latitude, longitude } }) => {
        bounds.extend({ lat: Number(latitude), lng: Number(longitude) })
      })

      if (this.renderDepotOnMap === true) {
        bounds.extend(this.depotLatLng)
      }

      return bounds
    },

    waypoints () {
      if (!this.google) {
        return []
      }

      return this.deliveryRun.stops.map(({ station: { latitude, longitude } }) => {
        return {
          location: { lat: Number(latitude), lng: Number(longitude) },
          stopover: true
        }
      })
    },

    chunkedWaypoints () {
      // google map limit waypoints to 25
      // for more than 25 waypoints
      // we need to chunk them
      // and perform multiple requests to google
      return chunk(this.waypoints, 25)
    },

    waypointsHash () {
      if (this.waypoints.length > 0) {
        return hash(this.waypoints)
      }

      return null
    }
  },

  watch: {
    triggerUpdateMapDirections () {
      if (this.triggerUpdateMapDirections) {
        this.triggerUpdateMapDirections = false

        if (this.deliveryRun.courier) {
          this.dontAskForDirections()
        } else {
          this.askForDirections()
        }
      }
    }
  },

  created () {
    if (this.deliveryRun.courier) {
      this.renderDepotOnMap = false
    } else {
      this.renderDepotOnMap = true
    }
  },

  mounted () {
    this.$refs.map.$mapPromise.then(async (map) => {
      map.fitBounds(this.bounds)

      this.map = map

      if (this.deliveryRun.courier) {
        return this.dontAskForDirections()
      }

      this.directions.service = new this.google.maps.DirectionsService()

      // if (await this.useCachedPolyline() === false) {
      this.askForDirections()
      // }
    })
  },

  methods: {
    /**
     * Decompresses the encoded polyline string from the database
     */
    decompress (encoded) {
      var precision = 6
      var len = encoded.length
      var index = 0
      var lat = 0
      var lng = 0
      var array = []
      precision = Math.pow(10, -precision)
      while (index < len) {
        var b
        var shift = 0
        var result = 0
        do {
          b = encoded.charCodeAt(index++) - 63
          result |= (b & 0x1f) << shift
          shift += 5
        } while (b >= 0x20)
        var dlat = ((result & 1) ? ~(result >> 1) : (result >> 1))
        lat += dlat
        shift = 0
        result = 0
        do {
          b = encoded.charCodeAt(index++) - 63
          result |= (b & 0x1f) << shift
          shift += 5
        } while (b >= 0x20)
        var dlng = ((result & 1) ? ~(result >> 1) : (result >> 1))
        lng += dlng
        array.push({ 'lat': lat * precision, 'lng': lng * precision })
      }
      return array
    },

    /**
     * Simply renders the stops on the map rather than the directions polyline.
     * Used for courier runs. No caching.
     */
    dontAskForDirections () {
      let order = 1

      this.renderStops({
        markers: this.waypoints.map(({ location }) => ({
          ...location,
          text: `${order++}`
        }))
      })
    },

    /**
     * Asks for optimised directions so we can update the order of our stops based
     * on what Google thinks is the quickest.
     */
    askForOptimisedDirections (done) {
      this.askForDirections(done, {
        optimizeWaypoints: true
      })
    },

    getWaypointOrigin (waypoints, chunkedWaypointsIndex) {
      if (chunkedWaypointsIndex === 0) {
        return this.depotLatLng
      }

      return waypoints[0].location
    },

    getWaypointDestination (waypoints, chunkedWaypointsIndex) {
      if (chunkedWaypointsIndex === this.chunkedWaypoints.length - 1) {
        return this.depotLatLng
      }

      return waypoints[waypoints.length - 1].location
    },

    /**
     * Asks for directions from Google.
     */
    askForDirections (done, options = {}) {
      let waypointRequests = this.chunkedWaypoints.map((waypoints, index) => {
        return new Promise((resolve, reject) => {
          this.directions.service.route(Object.assign({
            origin: this.getWaypointOrigin(waypoints, index),
            destination: this.getWaypointDestination(waypoints, index),
            waypoints,
            travelMode: 'DRIVING'
          }, options), (response, status) => {
            resolve({ response, status })
          })
        })
      })

      Promise.all(waypointRequests).then(responses => {
        // process response and generate payloads
        return responses
          .filter(({ response, status }) => status === 'OK')
          .map(({ response: { routes: [route] }, status }) => ({
            polyline: route.overview_polyline,
            distance: route.legs.reduce((totalDistance, { distance }) => totalDistance + distance.value, 0),
            duration: route.legs.reduce((totalDuration, { duration }) => totalDuration + duration.value, 0),
            markers: route.legs.slice(1).map((leg) => ({
              ...leg.start_location.toJSON()
            }))
          }))
          .reduce((data, payload) => {
            data.polylines.push(payload.polyline)
            return {
              distance: data.distance + payload.distance,
              duration: data.duration + payload.duration,
              markers: [...data.markers, ...payload.markers],
              polylines: data.polylines.slice()
            }
          }, {
            distance: 0,
            duration: 0,
            markers: [],
            polylines: []
          })
      }).then(payload => {
        // update marker text number
        payload.markers = payload.markers.map((marker, index) => ({
          ...marker,
          text: `${index + 1}`
        }))
        // draw on gmap
        // this.setCachedDirections(payload)
        this.renderStops(payload)
        this.renderPolyline(payload)
      }).catch()
    },

    renderPolyline ({ markers }) {
      if (this.polyline) {
        this.polyline.setMap(null)
      }

      const depotLatLng = {
        lat: parseFloat(process.env.VUE_APP_DEPOT_LATITUDE),
        lng: parseFloat(process.env.VUE_APP_DEPOT_LONGITUDE)
      }
      this.polyline = new this.google.maps.Polyline({
        path: ((this.deliveryRun.polylines) ? this.decompress(this.deliveryRun.polylines) : [{ ...depotLatLng }, ...markers.map(({ lat, lng }) => ({ lat, lng })), { ...depotLatLng }]),
        geodesic: true,
        map: this.map,
        strokeColor: '#31B0D5',
        strokeOpacity: 0.6,
        strokeWeight: 5
      })
    },

    renderStops ({ markers, distance, duration }) {
      const { map } = this

      if (this.markers.length) {
        this.markers.forEach(marker => marker.setMap(null))
        this.markers = []
      }

      if (distance && duration) {
        this.$emit('updated', { distance, duration })
      }

      markers.forEach(({ lat, lng, text }) => {
        this.markers.push(new this.google.maps.Marker({
          position: { lat, lng },
          label: {
            text,
            color: '#ffffff'
          },
          map
        }))
      })

      if (this.renderDepotOnMap === true) {
        this.markers.push(new this.google.maps.Marker({
          position: this.depotLatLng,
          label: {
            text: 'D',
            color: '#ffffff'
          },
          map
        }))
      }

      // Either center on the bounds with a predefined zoom, or, if we have several bounding
      // points we'll let Google zoom to fit the bounds automatically.
      const { north, south, east, west } = this.bounds.toJSON()

      if (north === south && east === west) {
        this.map.setCenter(this.bounds.getCenter())
        this.map.setZoom(15)
      } else {
        this.map.fitBounds(this.bounds, 25)
      }
    },

    debugBounds () {
      // eslint-disable-next-line no-new
      new this.google.maps.Rectangle({
        strokeColor: '#FF0000',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#FF0000',
        fillOpacity: 0.35,
        map: this.map,
        bounds: this.bounds
      })
    }
  }
}
</script>
